import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import subscriberService from "../requests/subscribers/SubscriberService";

const useSubscribers = (statsTotal = 0) => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 15,
    hasNext: false,
    hasPrev: false,
  });
  const [filters, setFilters] = useState({
    search: "",
    sortBy: "",
    sortOrder: "desc",
    dateFrom: "",
    dateTo: "",
    channel: "",
  });

  //* Cache for storing fetched data
  const cache = useRef(new Map());
  const searchTimeoutRef = useRef(null);

  //* Debounced search function
  const debouncedSearch = useCallback((searchTerm) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchTerm }));
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
    }, 500);
  }, []);

  //* Generate cache key
  const getCacheKey = useCallback(
    (page, limit, search, sortBy, sortOrder, dateFrom, dateTo, channel) => {
      return `${page}-${limit}-${search}-${sortBy}-${sortOrder}-${dateFrom}-${dateTo}-${channel}`;
    },
    []
  );

  // Compute pagination data based on stats total
  const computePaginationData = useCallback((page, limit, totalItems) => {
    const totalPages = Math.ceil(totalItems / limit);
    const currentPage = Math.max(Math.min(Math.max(page || 1, 1), totalPages), 1);
    const hasNext = currentPage < totalPages;
    const hasPrev = currentPage > 1;

    return {
      current_page: currentPage,
      total_pages: totalPages,
      total_items: totalItems,
      items_per_page: limit,
      has_next: hasNext,
      has_prev: hasPrev,
    };
  }, []);

  //* Fetch subscribers with caching
  const fetchSubscribers = useCallback(
    async (
      page = 1,
      limit = 15,
      search = "",
      sortBy = "",
      sortOrder = "desc",
      dateFrom = "",
      dateTo = "",
      channel = ""
    ) => {
      const safePage = Math.max(page || 1, 1);
      
      const cacheKey = getCacheKey(
        safePage,
        limit,
        search,
        sortBy,
        sortOrder,
        dateFrom,
        dateTo,
        channel
      );

      //* Check cache first
      if (cache.current.has(cacheKey)) {
        const cachedData = cache.current.get(cacheKey);
        setSubscribers(cachedData.data);
        setPagination(cachedData.pagination);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await subscriberService.getSubscribersPaginated(
          safePage,
          limit,
          search,
          sortBy,
          sortOrder,
          dateFrom,
          dateTo,
          channel
        );

        if (response.status_code === "000") {
          const hasActiveFilters = search || dateFrom || dateTo || channel;
          
          
          
          let totalItems;
          if (hasActiveFilters) {
            totalItems = response.pagination?.total_items || response.data.length;
          } else {
            totalItems = statsTotal || response.data.length;
          }
          
          const computedPagination = computePaginationData(safePage, limit, totalItems);
          
          setSubscribers(response.data);
          setPagination({
            currentPage: computedPagination.current_page,
            totalPages: computedPagination.total_pages,
            totalItems: computedPagination.total_items,
            itemsPerPage: computedPagination.items_per_page,
            hasNext: computedPagination.has_next,
            hasPrev: computedPagination.has_prev,
          });

          //* Cache the result (limit cache size to prevent memory issues)
          if (cache.current.size > 50) {
            const firstKey = cache.current.keys().next().value;
            cache.current.delete(firstKey);
          }
          cache.current.set(cacheKey, {
            data: response.data,
            pagination: {
              currentPage: computedPagination.current_page,
              totalPages: computedPagination.total_pages,
              totalItems: computedPagination.total_items,
              itemsPerPage: computedPagination.items_per_page,
              hasNext: computedPagination.has_next,
              hasPrev: computedPagination.has_prev,
            },
            timestamp: Date.now(),
          });
        } else {
          setError(response.reason);
          toast.error(response.reason || "Error fetching subscribers");
        }
      } catch (err) {
        setError("Network error occurred");
        toast.error("Network error occurred");
        console.error("Error fetching subscribers:", err);
      } finally {
        setLoading(false);
      }
    },
    [getCacheKey, computePaginationData, statsTotal]
  );

  //* Load subscribers when filters or pagination changes
  useEffect(() => {
    const currentPage = Math.max(pagination.currentPage || 1, 1);
    const itemsPerPage = pagination.itemsPerPage || 15;
    
    fetchSubscribers(
      currentPage,
      itemsPerPage,
      filters.search,
      filters.sortBy,
      filters.sortOrder,
      filters.dateFrom,
      filters.dateTo,
      filters.channel
    );
  }, [
    pagination.currentPage,
    pagination.itemsPerPage,
    filters.search,
    filters.sortBy,
    filters.sortOrder,
    filters.dateFrom,
    filters.dateTo,
    filters.channel,
    fetchSubscribers,
  ]);

  // Recalculate pagination when statsTotal changes
  useEffect(() => {
    if (statsTotal > 0) {
      const hasActiveFilters = filters.search || filters.dateFrom || filters.dateTo;
      
      // Only recalculate from stats total if no filters are applied
      if (!hasActiveFilters) {
        const computedPagination = computePaginationData(
          pagination.currentPage,
          pagination.itemsPerPage,
          statsTotal
        );
        setPagination({
          currentPage: computedPagination.current_page,
          totalPages: computedPagination.total_pages,
          totalItems: computedPagination.total_items,
          itemsPerPage: computedPagination.items_per_page,
          hasNext: computedPagination.has_next,
          hasPrev: computedPagination.has_prev,
        });
      }
    }
  }, [statsTotal, computePaginationData, filters.search, filters.dateFrom, filters.dateTo]);

  //* Clear cache when component unmounts or when search changes significantly
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Clear cache when filters change
  useEffect(() => {
    cache.current.clear();
  }, [filters.search, filters.dateFrom, filters.dateTo, filters.sortBy, filters.sortOrder, filters.channel]);

  //* Change page
  const changePage = useCallback((newPage) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  }, []);

  //* Change items per page
  const changeItemsPerPage = useCallback((newLimit) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
      itemsPerPage: newLimit,
    }));
  }, []);

  //* Update sort
  const updateSort = useCallback((sortBy, sortOrder) => {
    setFilters((prev) => ({ ...prev, sortBy, sortOrder }));
  }, []);

  //* Update date filters
  const updateDateFilter = useCallback((dateFrom, dateTo) => {
    setFilters((prev) => ({ ...prev, dateFrom, dateTo }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  //* Update channel filter
  const updateChannelFilter = useCallback((channel) => {
    setFilters((prev) => ({ ...prev, channel }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  //* Clear date filters
  const clearDateFilter = useCallback(() => {
    setFilters((prev) => ({ ...prev, dateFrom: "", dateTo: "" }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  //* Clear all filters
  const clearAllFilters = useCallback(() => {
    setFilters({
      search: "",
      sortBy: "",
      sortOrder: "desc",
      dateFrom: "",
      dateTo: "",
      channel: "",
    });
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  //* Bulk operations
  const bulkUpdate = useCallback(
    async (subscriberIds, action) => {
      setLoading(true);
      try {
        const response = await subscriberService.bulkUpdateSubscribers(
          subscriberIds,
          action
        );

        if (response.status_code === "000") {
          toast.success(
            response.reason || "Bulk operation completed successfully"
          );
          //* Refresh current page data
          fetchSubscribers(
            pagination.currentPage,
            pagination.itemsPerPage,
            filters.search,
            filters.sortBy,
            filters.sortOrder,
            filters.dateFrom,
            filters.dateTo,
            filters.channel
          );
          return true;
        } else {
          toast.error(response.reason || "Bulk operation failed");
          return false;
        }
      } catch (err) {
        toast.error("Error performing bulk operation");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [
      pagination.currentPage,
      pagination.itemsPerPage,
      filters.search,
      filters.sortBy,
      filters.sortOrder,
      filters.dateFrom,
      filters.dateTo,
      filters.channel,
      fetchSubscribers,
    ]
  );

  // Export subscribers
  const exportSubscribers = useCallback(
    async (format = "csv") => {
      try {
        const response = await subscriberService.exportSubscribers(
          format,
          filters
        );

        if (response.status_code === "000") {
          //* Create download link
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", response.filename);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);

          toast.success("Export completed successfully");
          return true;
        } else {
          toast.error(response.reason || "Export failed");
          return false;
        }
      } catch (err) {
        toast.error("Error exporting subscribers");
        return false;
      }
    },
    [filters]
  );

  // Clear cache
  // const clearCache = useCallback(() => {
  //   cache.current.clear();
  // }, []);

  return {
    subscribers,
    loading,
    error,
    pagination,
    filters,
    statsTotal,
    debouncedSearch,
    changePage,
    changeItemsPerPage,
    updateSort,
    updateDateFilter,
    updateChannelFilter,
    clearDateFilter,
    clearAllFilters,
    bulkUpdate,
    exportSubscribers,
    refetch: () =>
      fetchSubscribers(
        pagination.currentPage,
        pagination.itemsPerPage,
        filters.search,
        filters.sortBy,
        filters.sortOrder,
        filters.dateFrom,
        filters.dateTo,
        filters.channel
      ),
  };
};

export default useSubscribers;
